import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserModal = ({ isOpen, onClose, user, onSave, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'operator',
    status: 'active',
    dailyLimit: 50,
    twoFactorEnabled: false,
    password: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user && mode === 'edit') {
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        role: user?.role || 'operator',
        status: user?.status || 'active',
        dailyLimit: user?.dailyLimit || 50,
        twoFactorEnabled: user?.twoFactorEnabled || false,
        password: '',
        confirmPassword: ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        role: 'operator',
        status: 'active',
        dailyLimit: 50,
        twoFactorEnabled: false,
        password: '',
        confirmPassword: ''
      });
    }
    setErrors({});
  }, [user, mode, isOpen]);

  const roleOptions = [
    { value: 'admin', label: 'Administrador', description: 'Acceso completo al sistema' },
    { value: 'operator', label: 'Operador', description: 'Consultas VIN y funciones básicas' },
    { value: 'auditor', label: 'Auditor', description: 'Solo lectura y auditoría' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Activo' },
    { value: 'inactive', label: 'Inactivo' },
    { value: 'blocked', label: 'Bloqueado' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Formato de email inválido';
    }

    if (mode === 'create') {
      if (!formData?.password) {
        newErrors.password = 'La contraseña es requerida';
      } else if (formData?.password?.length < 8) {
        newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
      }

      if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    }

    if (formData?.dailyLimit < 1 || formData?.dailyLimit > 50) {
      newErrors.dailyLimit = 'El límite debe estar entre 1 y 50';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      setErrors({ submit: 'Error al guardar el usuario. Intenta nuevamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-card rounded-lg shadow-elevation-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name={mode === 'create' ? "UserPlus" : "Edit"} size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {mode === 'create' ? 'Agregar Nuevo Usuario' : 'Editar Usuario'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {mode === 'create' ?'Completa la información para crear un nuevo usuario' :'Modifica la información del usuario seleccionado'
                }
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors?.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-red-600" />
                <span className="text-sm text-red-700">{errors?.submit}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Nombre Completo"
              type="text"
              placeholder="Ej: Juan Pérez García"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <Input
              label="Correo Electrónico"
              type="email"
              placeholder="usuario@empresa.com"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Rol del Usuario"
              description="Define los permisos y accesos del usuario"
              options={roleOptions}
              value={formData?.role}
              onChange={(value) => handleInputChange('role', value)}
              error={errors?.role}
              required
            />

            <Select
              label="Estado"
              options={statusOptions}
              value={formData?.status}
              onChange={(value) => handleInputChange('status', value)}
              error={errors?.status}
              required
            />
          </div>

          <Input
            label="Límite de Consultas Diarias"
            type="number"
            min="1"
            max="50"
            placeholder="50"
            description="Número máximo de consultas VIN permitidas por día"
            value={formData?.dailyLimit}
            onChange={(e) => handleInputChange('dailyLimit', parseInt(e?.target?.value) || 0)}
            error={errors?.dailyLimit}
            required
          />

          {mode === 'create' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Contraseña"
                type="password"
                placeholder="Mínimo 8 caracteres"
                value={formData?.password}
                onChange={(e) => handleInputChange('password', e?.target?.value)}
                error={errors?.password}
                required
              />

              <Input
                label="Confirmar Contraseña"
                type="password"
                placeholder="Repite la contraseña"
                value={formData?.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                error={errors?.confirmPassword}
                required
              />
            </div>
          )}

          <div className="border-t border-border pt-6">
            <Checkbox
              label="Habilitar Autenticación de Dos Factores (2FA)"
              description="Requiere verificación adicional para mayor seguridad"
              checked={formData?.twoFactorEnabled}
              onChange={(e) => handleInputChange('twoFactorEnabled', e?.target?.checked)}
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isLoading}
              iconName={mode === 'create' ? "UserPlus" : "Save"}
              iconPosition="left"
              iconSize={16}
            >
              {mode === 'create' ? 'Crear Usuario' : 'Guardar Cambios'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;